import React, { useState } from 'react';
import { RefreshCw, Edit2, Check, X } from 'lucide-react';
import { recalculateBPM, updateTrack } from '../utils/api';
import { toast } from 'react-hot-toast';

const TrackBPM = ({ track, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedBPM, setEditedBPM] = useState(track.bpm || '');
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [isIgnored, setIsIgnored] = useState(track.bpm_ignore || false);

  const handleRecalculate = async () => {
    setIsRecalculating(true);
    try {
      const response = await recalculateBPM(track.id);
      toast.success(`BPM recalculé: ${response.data.bpm} BPM`);
      if (onUpdate) onUpdate(response.data);
    } catch (error) {
      console.error('Error recalculating BPM:', error);
      toast.error('Erreur lors du recalcul du BPM');
    } finally {
      setIsRecalculating(false);
    }
  };

  const handleSaveBPM = async () => {
    const bpmValue = parseFloat(editedBPM);
    
    if (isNaN(bpmValue) || bpmValue < 60 || bpmValue > 200) {
      toast.error('BPM invalide (60-200)');
      return;
    }

    try {
      const response = await updateTrack(track.id, { bpm: bpmValue });
      toast.success(`BPM modifié: ${bpmValue} BPM`);
      setIsEditing(false);
      if (onUpdate) onUpdate(response.data);
    } catch (error) {
      console.error('Error updating BPM:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  const handleToggleIgnore = async () => {
    const newIgnoreValue = !isIgnored;
    
    try {
      const response = await updateTrack(track.id, { bpm_ignore: newIgnoreValue });
      setIsIgnored(newIgnoreValue);
      toast.success(newIgnoreValue ? 'BPM ignoré' : 'BPM activé');
      if (onUpdate) onUpdate(response.data);
    } catch (error) {
      console.error('Error toggling BPM ignore:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedBPM(track.bpm || '');
  };

  // Don't show anything if no BPM and not uploaded file
  if (!track.bpm && track.source_type !== 'upload') {
    return null;
  }

  return (
    <div className="flex items-center gap-2 text-xs">
      {/* BPM Display/Edit */}
      {!isEditing ? (
        <div className="flex items-center gap-1.5">
          <span className={`font-mono ${
            isIgnored 
              ? 'text-sunset-orange/30 line-through' 
              : track.bpm_manually_edited 
                ? 'text-green-400' 
                : 'text-sunset-orange/70'
          }`}>
            {track.bpm ? `${track.bpm.toFixed(1)} BPM` : 'BPM ?'}
          </span>
          
          {/* Edit button */}
          {track.bpm && (
            <button
              onClick={() => setIsEditing(true)}
              className="p-0.5 hover:bg-purple-500/20 rounded transition-colors"
              title="Modifier le BPM"
            >
              <Edit2 className="w-3 h-3 text-sunset-orange/60" />
            </button>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-1">
          <input
            type="number"
            value={editedBPM}
            onChange={(e) => setEditedBPM(e.target.value)}
            className="w-16 px-1.5 py-0.5 bg-purple-900/30 border border-sunset-orange/30 rounded text-xs text-white focus:outline-none focus:border-purple-400"
            min="60"
            max="200"
            step="0.1"
            autoFocus
          />
          <button
            onClick={handleSaveBPM}
            className="p-0.5 hover:bg-green-500/20 rounded transition-colors"
            title="Enregistrer"
          >
            <Check className="w-3 h-3 text-green-400" />
          </button>
          <button
            onClick={handleCancelEdit}
            className="p-0.5 hover:bg-red-500/20 rounded transition-colors"
            title="Annuler"
          >
            <X className="w-3 h-3 text-red-400" />
          </button>
        </div>
      )}

      {/* Recalculate button */}
      {track.source_type === 'upload' && !isEditing && (
        <button
          onClick={handleRecalculate}
          disabled={isRecalculating}
          className="p-0.5 hover:bg-purple-500/20 rounded transition-colors disabled:opacity-50"
          title="Recalculer le BPM"
        >
          <RefreshCw className={`w-3 h-3 text-sunset-orange/60 ${isRecalculating ? 'animate-spin' : ''}`} />
        </button>
      )}

      {/* Ignore BPM checkbox */}
      {track.bpm && !isEditing && (
        <label className="flex items-center gap-1 cursor-pointer" title="Ignorer le BPM">
          <input
            type="checkbox"
            checked={isIgnored}
            onChange={handleToggleIgnore}
            className="w-3 h-3 rounded border-sunset-orange/30 bg-purple-900/30 text-purple-500 focus:ring-purple-500 focus:ring-offset-0 cursor-pointer"
          />
          <span className="text-sunset-orange/50 text-[10px]">Ignore</span>
        </label>
      )}
    </div>
  );
};

export default TrackBPM;
